import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { openTopLevel, planGateAllows } from '../lib/embedded';
import { HelpTip, PageHeader } from './Page';
import { Spinner } from './States';
import { useQuery } from '../lib/query';
import { money, num } from '../lib/format';
import { PUBLIC_PLANS_PATH, type PublicPlan, type PublicPlansResponse } from '../pages/site/publicData';

/**
 * App Home without a usable plan (a new install, a reinstall, a plan not approved yet): Shopify asks
 * apps to send these merchants to plan selection first. Every page but the plan-free ones (plans,
 * settings, support) shows this screen instead. The plan state comes from the App Home bootstrap (the
 * same subscription record billing uses); returning from Shopify's plan page reloads App Home, which
 * reads it again.
 */
export function PlanGate() {
  const { embedded } = useAuth();
  const location = useLocation();
  if (planGateAllows(embedded?.plan, location.pathname)) {
    // Shopify is confirming the plan (a trial that just ended, or a verification being renewed).
    if (!embedded?.plan?.confirming) return <Outlet />;
    return (
      <>
        <div className="p-banner p-banner--attention tip-line" role="status" data-testid="plan-confirming">
          <Spinner size={14} />
          <span>Confirming your plan…</span>
          <HelpTip topic="Plan confirmation" help="Shopify is confirming your plan. Creating art resumes as soon as it does." />
        </div>
        <Outlet />
      </>
    );
  }
  return <ChoosePlanScreen selectionUrl={embedded?.plan?.selectionUrl ?? null} />;
}

/** One plan's terms as Shopify's plan page will charge them: price, included generations, the free trial. */
export function planTermsLine(p: PublicPlan): string {
  const trial = p.trialDays ? ` · ${p.trialDays}-day free trial` : '';
  return `${money(p.monthlyPriceCents, p.currency)}/month · ${num(p.includedCredits)} generations included${trial}`;
}

/**
 * Sol pre-launch P3: the terms before the merchant leaves for Shopify (price and trial days per plan, from the
 * public catalog that mirrors Shopify App Pricing). Shopify's page stays the one that approves the charge.
 */
function PlanTerms() {
  const plans = useQuery<PublicPlansResponse>(PUBLIC_PLANS_PATH);
  const list = plans.data?.plans ?? [];
  if (!list.length) return null;
  // Plan section 8 (multi-variant plan): the recommendation before the merchant leaves for Shopify, with each
  // plan's real extra-generation price in its tooltip. Turned on later in Billing (consent unchecked there).
  const prices = list
    .filter((p) => p.overagePriceCents > 0)
    .map((p) => `${p.name}: ${money(p.overagePriceCents, p.currency)}`)
    .join(' · ');
  return (
    <>
      <ul className="stack stack--sm" style={{ margin: 0, paddingLeft: 18 }} data-testid="plan-gate-plans">
        {list.map((p) => (
          <li key={p.key}>
            <strong>{p.name}</strong> <span className="p-subdued">{planTermsLine(p)}</span>
          </li>
        ))}
      </ul>
      <p className="small title-row" data-testid="plan-gate-extras" style={{ margin: 0 }}>
        <span>Tip: once your plan is active, turn on extra generations with a spending cap so personalization keeps working after your included credits run out.</span>
        <HelpTip topic="Extra generations" help={`${prices ? `Price per extra generation — ${prices}. ` : ''}Extras apply to paid billing cycles only: trial limits still apply. New art pauses at your cap. Products that require art stay blocked while new art is paused.`} />
      </p>
    </>
  );
}

export function ChoosePlanScreen({ selectionUrl }: { selectionUrl: string | null }) {
  const navigate = useNavigate();
  const choose = () => {
    // Shopify's plan page lives in the admin itself: App Bridge takes the whole admin there (_top).
    if (selectionUrl) openTopLevel(selectionUrl);
    else navigate('/app/billing');
  };
  return (
    <>
      <PageHeader title="Choose your plan" />
      <div className="p-layout">
        <section className="p-card" aria-labelledby="choose-plan-title" data-testid="plan-gate">
          <div className="stack stack--sm">
            <h2 id="choose-plan-title">Choose a plan to start using Perzo</h2>
            <p className="p-subdued">
              Your shoppers can create art once your store has a plan. New stores get a free trial; you approve the plan on Shopify&rsquo;s plan page.
            </p>
            <PlanTerms />
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn--primary" onClick={choose}>
                Choose your plan
              </button>
              <button type="button" className="btn btn--secondary" onClick={() => window.location.reload()}>
                I already chose a plan
              </button>
            </div>
            <p className="p-subdued small">
              Meanwhile you can open <Link to="/app/settings">Settings</Link> or <Link to="/app/support">Support</Link>.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
